import React, { useEffect, useState, useMemo } from 'react';
import { Container, Row, Col, Card, Table, Alert } from 'react-bootstrap';
import { Link, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { formatCurrency } from '../utils/currency';
import { shippingDiscountOf, shippingOfferName, namedPromotions } from '../utils/promotionDisplay';
import { fetchOrder, createPaymentIntent, reconcilePaymentIntent, getOrderPaymentIntent, ensureFreshPaymentIntent, clearCartAfterPayment } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import useNotification from '../hooks/useNotification';
import stripePromise from '../utils/stripe';
import PaymentForm from '../components/Payments/PaymentForm';
import { buildAssetUrl, retryThenTile } from '../utils/imageResolver';
import { NO_PHOTO_SRC } from '../constants/noPhoto';
import '../styles/checkout.css';
import '../styles/cart.css'; // #90b shared .discount-breakdown classes
import { trackCheckoutEvent } from '../utils/telemetry';
import FroggySpinner from '../components/common/FroggySpinner';
import ShippingTemperatureNotice from '../components/common/ShippingTemperatureNotice';
import { DELIVERY_HUB, DELIVERY_HOME, deliveryMethodOfOrder } from '../constants/shippingTemperatures';
import { memberDisplayLabel } from '../components/MyStore/CreateLotModal';

// Helper function to convert API status codes to human-readable labels
const statusLabel = (status) => {
  switch (status) {
    case 'AWAITING_PAYMENT':
      return 'Awaiting payment';
    case 'PAID':
      return 'Paid';
    case 'SCHEDULED':
      return 'Scheduled';
    case 'SHIPPED':
      return 'Shipped';
    case 'DELIVERED':
      return 'Delivered';
    case 'AWAITING_CUSTOMER_SCHEDULING':
      return 'Awaiting schedule';
    case 'AWAITING_STORE_SCHEDULING':
      return 'Being prepared';
    case 'CANCELLED':
      return 'Cancelled';
    default:
      return status?.replace(/_/g, ' ').toLowerCase() || 'Unknown';
  }
};

// I-02e #61 / adv-review S1: the shipping discount lives only in the
// promotion usage rows (pending: {usages}, paid: {promotions}) — no Order
// column carries it. Shared helper (utils/promotionDisplay), used by the
// gate and the line; this page's local copy was the oldest of the three.

const CheckoutPaymentPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { clearCart } = useCart();
  const { showNotification } = useNotification();
  
  const [orderData, setOrderData] = useState(null);
  const [quoteData, setQuoteData] = useState(null);
  const [addressData, setAddressData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shippingMismatch, setShippingMismatch] = useState(false);
  const [orderStatus, setOrderStatus] = useState(null);
  const [usedFallback, setUsedFallback] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentStep, setPaymentStep] = useState('init'); // 'init', 'reservation', 'payment', 'polling', 'success'
  const [currentPiId, setCurrentPiId] = useState(null);
  const [currentPiStatus, setCurrentPiStatus] = useState(null);

  // Reset state when entering payment step
  useEffect(() => {

    // Reset state when entering payment step to ensure clean state
    if (paymentStep === 'payment' && clientSecret) {
      setIsProcessingPayment(false); // Ensure form is not stuck in processing
      setPaymentError(null); // Clear any previous payment errors
    }
  }, [paymentStep, isProcessingPayment, clientSecret]);
  const [stripeConfigError, setStripeConfigError] = useState(false);
  const [lastPaymentAttempt, setLastPaymentAttempt] = useState(0);

  // Ensure page is scrollable on mount
  useEffect(() => {
    document.body.classList.add('checkout-page');
    return () => {
      document.body.classList.remove('checkout-page');
    };
  }, []);

  useEffect(() => {
    // Prevent auto-refresh during payment flow
    if (paymentStep === 'payment' || paymentStep === 'polling' || isProcessingPayment) {
      return;
    }

    const initializePage = async () => {
      const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      
      // Reset payment state on page init
      setIsProcessingPayment(false);
      setPaymentStep('init');
      
      // Check if user is authenticated
      if (!isAuthenticated) {
        // User not authenticated, redirect to login
        showNotification('Please log in to continue with your order.', 'warning');
        navigate('/login');
        return;
      }

      // Step 1: Detect orderId from multiple sources
      let orderId = null;
      
      // First try navigation state
      if (location.state?.order?.id) {
        orderId = location.state.order.id;
        // Detected orderId from navigation state
      } 
      // Then try URL params
      else if (searchParams.get('orderId')) {
        orderId = searchParams.get('orderId');
        // Detected orderId from URL params
      }
      // Finally try sessionStorage
      else {
        try {
          const savedData = sessionStorage.getItem('checkout-order-data');
          if (savedData) {
            const parsed = JSON.parse(savedData);
            orderId = parsed.orderId;
            // Detected orderId from sessionStorage
          }
        } catch (err) {
          // Failed to parse sessionStorage
        }
      }

      // If no orderId found, bounce back to address step
      if (!orderId) {
        // No orderId found, redirect to address step
        showNotification("We couldn't find that order. Please re-enter your address.", 'error');
        navigate('/checkout/address');
        return;
      }

      // Step 2: Set fallback data for initial paint (if available)
      const navigationState = location.state || {};
      if (navigationState.order || navigationState.quote) {
        // Using navigation state as fallback for initial paint
        setOrderData(navigationState.order);
        setQuoteData(navigationState.quote);
        setAddressData(navigationState.addresses);
        setUsedFallback(true);
      } else {
        // Try sessionStorage fallback
        try {
          const savedData = sessionStorage.getItem('checkout-order-data');
          if (savedData) {
            const parsed = JSON.parse(savedData);
            if (parsed.orderId === orderId) {
              // Using sessionStorage as fallback for initial paint
              setOrderData(parsed.orderSummary);
              setQuoteData(parsed.quote);
              setAddressData(parsed.addresses);
              setUsedFallback(true);
            }
          }
        } catch (err) {
          // Failed to use sessionStorage fallback
        }
      }

      // Step 3: Fetch fresh order data from server (source of truth)
      try {
        // Fetching fresh order data from server
        setLoading(true);
        setError(null);
        
        const response = await fetchOrder(orderId);
        // Fresh order data received
        
        // Overwrite local state with server data (source of truth)
        setOrderData(response.order);
        setOrderStatus(response.order.status);
        
        // Check if order is already paid and skip to success state
        if (response.order.payment_status === 'PAID') {
          // Order is already paid, showing success state
          setPaymentStep('success');
          setIsProcessingPayment(false);  // Ensure processing is not stuck
          setLoading(false);
          return;
        }
        
        // If we used fallback data and server data differs, log it
        if (usedFallback) {
          // Server data overrode fallback data
        }
        
        // Clear any previous errors
        setError(null);
        
        // Check for Stripe configuration
        if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
          setStripeConfigError(true);
        }
        
        // If order is AWAITING_PAYMENT, the user can manually initiate payment flow
        // Don't auto-initiate to avoid setting isProcessingPayment prematurely
        if (response.order.status === 'AWAITING_PAYMENT') {
          // Order ready for payment - user can click Begin Payment
        }
        
      } catch (err) {
        // Error fetching order from server
        
        if (err.response?.status === 404) {
          showNotification("We couldn't find that order. Please re-enter your address.", 'error');
          navigate('/checkout/address');
          return;
        } else {
          setError('Unable to load order details. Please try again or return to your cart.');
          // Don't navigate away on other errors - let user retry
        }
      } finally {
        setLoading(false);
      }
    };

    initializePage();
  }, [location.state, searchParams, isAuthenticated, navigate, showNotification]);


  const initiatePaymentFlow = async (orderId) => {
    // Prevent duplicate calls with debouncing
    const now = Date.now();
    if (isProcessingPayment || paymentStep !== 'init' || (now - lastPaymentAttempt) < 2000) {
      return;
    }

    setLastPaymentAttempt(now);

    // Track payment initiated event
    trackCheckoutEvent('checkout_payment_initiated', orderId, {
      order_total: orderData?.order_total,
    });

    try {
      setIsProcessingPayment(true);
      setPaymentError(null);
      
      // Get fresh payment intent (handles reuse/creation)
      let paymentIntent;
      try {
        paymentIntent = await getOrderPaymentIntent(orderId);
        
        // Store PI state for tracking and double-confirm prevention
        setCurrentPiId(paymentIntent.id);
        setCurrentPiStatus(paymentIntent.status);
        setClientSecret(paymentIntent.client_secret);
        setPaymentStep('payment');
        
        // Payment intent is already succeeded but DON'T auto-start polling
        // User must still click Pay button to complete the flow
        
      } catch (intentError) {
        // S3-01.4b: the Begin Payment endpoint now 422s stale quotes so
        // customers hear it BEFORE typing card details (the Pay-click
        // gate still backstops the charge itself).
        if (intentError.response?.data?.error === 'SHIPPING_QUOTE_STALE') {
          const staleMsg = 'Your shipping quote has expired — go back to the shipping step to refresh it, then return here to pay.';
          setPaymentError(staleMsg);
          showNotification(staleMsg, 'error');
        } else {
          setPaymentError(`Payment initialization failed: ${intentError.message}`);
          showNotification('Failed to initialize payment. Please try again.', 'error');
        }
        setPaymentStep('init');
        setIsProcessingPayment(false);
        return;
      }
      
      // Success - ready for payment
      setIsProcessingPayment(false); // Allow payment form to be used
      
    } catch (error) {
      setPaymentError('An unexpected error occurred. Please try again.');
      showNotification('Something went wrong. Please try again.', 'error');
      setPaymentStep('init');
      setIsProcessingPayment(false);
    }
  };

  const startOrderStatusPolling = (orderId, paymentIntentId = null) => {
    setIsProcessingPayment(true);
    
    // Exponential backoff polling: 1s, 2s, 4s (max ~7s total)
    const pollIntervals = [1000, 2000, 4000]; // Exponential backoff
    let pollIndex = 0;
    
    const pollOrderStatus = async () => {
      try {
        const response = await fetchOrder(orderId);
        
        // Check payment_status field specifically for payment completion
        if (response.order.payment_status === 'PAID') {
          setPaymentStep('success');
          setOrderData(response.order);
          setIsProcessingPayment(false); // Now safe to reset
          showNotification('Payment successful! Thank you for your order.', 'success');
          
          // Redirect to order confirmation page
          setTimeout(() => {
            navigate(`/order-confirmation/${orderId}`);
          }, 2000);
          return; // Stop polling - success!
        }
        
        // Continue polling with exponential backoff
        if (pollIndex < pollIntervals.length - 1) {
          pollIndex++;
          const nextInterval = pollIntervals[pollIndex];
          setTimeout(pollOrderStatus, nextInterval);
        } else {
          // Timeout after ~7s - try reconciliation
          await attemptPaymentReconciliation(orderId, paymentIntentId);
        }
      } catch (error) {
        // Don't show success on error - show neutral message
        setIsProcessingPayment(false);
        showNotification('Still processing your payment. We\'ll email you once confirmed.', 'info');
      }
    };
    
    // Start polling after first interval
    setTimeout(pollOrderStatus, pollIntervals[0]);
  };

  const attemptPaymentReconciliation = async (orderId, paymentIntentId = null) => {
    try {
      // Use the passed paymentIntentId or fall back to orderData.payment_intent_id
      const piIdToUse = paymentIntentId || orderData?.payment_intent_id;
      
      // GUARDRAIL: Only attempt reconciliation if payment_intent_id exists
      if (!piIdToUse) {
        setIsProcessingPayment(false);
        showNotification('Payment is being processed. Please refresh the page or try again in a moment.', 'info');
        // Don't set to success - stay in current state to allow retry
        return;
      }

      const response = await reconcilePaymentIntent(piIdToUse);

      if (response.reconciled && response.order.payment_status === 'PAID') {
        setPaymentStep('success');
        setOrderData(response.order);
        setIsProcessingPayment(false);
        showNotification('Payment confirmed! Thank you for your order.', 'success');
        
        // Backend-led cart clearing: call dedicated endpoint after successful payment
        try {
          const cartClearResult = await clearCartAfterPayment(orderId);
          
          if (cartClearResult.cleared) {
            // Clear frontend cart state to match backend
            localStorage.removeItem('cartId');
            localStorage.removeItem('cartItems');
            
            try {
              await clearCart();
            } catch (cartError) {
              // Don't fail the payment flow for cart clearing issues
            }
          }
        } catch (cartClearError) {
          // Don't fail the payment flow for cart clearing issues
        }
        
        // Redirect to order confirmation page
        setTimeout(() => {
          navigate(`/order-confirmation/${orderId}`);
        }, 2000);
      } else {
        setIsProcessingPayment(false);
        showNotification('Payment is being processed. Please refresh the page or check back in a moment.', 'info');
        // Don't set to success unless order is actually PAID
      }
    } catch (error) {
      setIsProcessingPayment(false);
      showNotification('Unable to verify payment status. Please refresh the page or contact support.', 'warning');
      // Don't set to success on error
    }
  };

  const handlePaymentSuccess = async (paymentIntentFromStripe = null) => {
    // Get the payment intent ID for polling (either from Stripe or current state)
    const paymentIntentIdForPolling = paymentIntentFromStripe?.id || currentPiId;

    // Track payment success event
    trackCheckoutEvent('checkout_payment_success', orderData?.id, {
      order_total: orderData?.order_total,
      payment_intent_id: paymentIntentIdForPolling,
    });

    // Update PI status and orderData with new payment_intent_id if we received it from Stripe
    if (paymentIntentFromStripe) {
      setCurrentPiStatus(paymentIntentFromStripe.status);
      setCurrentPiId(paymentIntentFromStripe.id);

      // Update orderData with new payment_intent_id so reconciliation can work
      setOrderData(prev => ({
        ...prev,
        payment_intent_id: paymentIntentFromStripe.id
      }));
    }
    
    // Guard: If PI is already succeeded, skip polling and go straight to reconciliation
    if (paymentIntentFromStripe?.status === 'succeeded') {
      setPaymentStep('polling');
      setIsProcessingPayment(true);
      showNotification('Processing payment... Please wait while we update your order.', 'info');
      
      // Go directly to reconciliation instead of polling
      await attemptPaymentReconciliation(orderData.id, paymentIntentIdForPolling);
      return;
    }
    
    // Immediately set polling state to prevent double submit
    setPaymentStep('polling');
    setIsProcessingPayment(true); // Keep button disabled during polling
    
    // Show processing toast
    showNotification('Processing payment... Please wait while we update your order.', 'info');
    
    // Start polling for order status update - pass PI ID directly to avoid state timing issues
    startOrderStatusPolling(orderData.id, paymentIntentIdForPolling);
  };

  const handlePaymentError = (error) => {
    // Track payment failure event
    trackCheckoutEvent('checkout_payment_failed', orderData?.id, {
      error_message: error.message || 'Unknown error',
    });

    setPaymentError(error.message || 'Payment failed. Please try again.');
    setIsProcessingPayment(false);
    setPaymentStep('payment');
  };

  if (loading) {
    return (
      <Container className="py-4">
        <div className="text-center">
          <h3>Loading Order Details...</h3>
          <FroggySpinner size="lg" label="Loading" />
        </div>
      </Container>
    );
  }

  if (error || !orderData) {
    return (
      <Container className="py-4">
        <div className="text-center">
          <h3>Order Details</h3>
          <Alert variant="warning" className="mt-4">
            <Alert.Heading>Unable to load order details</Alert.Heading>
            <p>
              {error || "We couldn't find your order information. This usually happens when navigating directly to this page."}
            </p>
            <hr />
            <div className="d-flex gap-2 justify-content-center">
              <Link to="/cart" className="ff-btn ff-btn--primary">
                Return to Cart
              </Link>
              <Link to="/" className="ff-btn ff-btn--secondary">
                Continue Shopping
              </Link>
            </div>
          </Alert>
        </div>
      </Container>
    );
  }

  return (
    <div className="checkout-inner">
      <Container className="py-4 checkout-container">
        <h2 className="checkout-heading text-center mb-4">Checkout - Payment</h2>
        
        
        <Row>
          <Col md={8}>
            <Card className="mb-4">
              <Card.Header>
                <h5>Order Status</h5>
              </Card.Header>
              <Card.Body>
                <p className="mb-3">
                  Your order has been created. Please complete payment to finalize your purchase.<br/>
                  After payment, we'll email you to schedule delivery/pickup details.
                </p>
                <div className="mt-3">
                  <small className="text-muted small">Order ID: {orderData.id}</small>
                </div>
                <div className="mt-2">
                  <span
                    className="ff-badge ff-badge--info"
                    role="status"
                    aria-label={`Order status: ${statusLabel(orderData.status || 'AWAITING_PAYMENT')}`}
                  >
                    {statusLabel(orderData.status || 'AWAITING_PAYMENT')}
                  </span>
                </div>
              </Card.Body>
            </Card>

            {/* Shipping Details */}
            <Card className="mb-4">
              <Card.Header>
                <h5>📦 Shipping Information</h5>
              </Card.Header>
              <Card.Body>
                {addressData?.shipToHub ? (
                  <div>
                    <p><strong>Delivery Method:</strong> FedEx Hub Pickup</p>
                    <p><strong>Hub Address:</strong></p>
                    <address>
                      {addressData.shipping?.address_line_1}<br />
                      {addressData.shipping?.address_line_2 && (
                        <>{addressData.shipping.address_line_2}<br /></>
                      )}
                      {addressData.shipping?.city}, {addressData.shipping?.state} {addressData.shipping?.zip}
                    </address>
                  </div>
                ) : (
                  <div>
                    <p><strong>Delivery Method:</strong> Home Delivery</p>
                    <p><strong>Shipping Address:</strong></p>
                    <address>
                      {addressData?.shipping?.address_line_1}<br />
                      {addressData?.shipping?.address_line_2 && (
                        <>{addressData.shipping.address_line_2}<br /></>
                      )}
                      {addressData?.shipping?.city}, {addressData?.shipping?.state} {addressData?.shipping?.zip}
                    </address>
                  </div>
                )}
              </Card.Body>
            </Card>
            
            {/* Temperature Policy Notice */}
            <Card className="mb-4 policy-card">
              <Card.Body>
                <h6 className="policy-card-title mb-2">🌡️ Temperature Policy</h6>
                {/* Fix batch 42: the same reading as the Delivery Method line
                    above; with no saved checkout data (a refresh, a bookmarked
                    ?orderId=) the ORDER's own delivery type decides, and an
                    unreadable one shows both windows rather than the home one
                    (batch 42 review). */}
                <ShippingTemperatureNotice
                  method={addressData
                    ? (addressData.shipToHub ? DELIVERY_HUB : DELIVERY_HOME)
                    : deliveryMethodOfOrder(orderData)}
                  lineClassName="small mb-2"
                  label={null}
                />
                <p className="small mb-0">
                  <button
                    type="button"
                    className="ff-btn ff-btn--link p-0 align-baseline text-decoration-underline"
                    onClick={() => navigate('/shipping-policy')}
                    style={{fontSize: 'inherit'}}
                  >
                    View shipping policy
                  </button>
                </p>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            {/* Order Summary */}
            <Card>
              <Card.Header>
                <h5>💰 Order Summary</h5>
              </Card.Header>
              <Card.Body>
                {orderData.items && orderData.items.length > 0 ? (
                  <Table size="sm" className="mb-3">
                    <tbody>
                      {orderData.items.map((item, index) => (
                        <tr key={index}>
                          <td>
                            <div className="d-flex align-items-center">
                              {(() => {
                                const variation = item.product_variation;
                                const morph = variation?.morph;
                                const species = variation?.species || variation?.product?.species;
                                
                                // Full cascade hierarchy: variation → morph → species → placeholder
                                let imageUrl = null;
                                let source = 'placeholder';
                                
                                // Priority 1: Variation images
                                if (variation?.cover_display_url) {
                                  imageUrl = buildAssetUrl(variation.cover_display_url);
                                  source = 'variation.cover_display_url';
                                } else if (variation?.cover_thumb_url) {
                                  imageUrl = buildAssetUrl(variation.cover_thumb_url);
                                  source = 'variation.cover_thumb_url';
                                } else if (variation?.image_url) {
                                  imageUrl = buildAssetUrl(variation.image_url);
                                  source = 'variation.image_url';
                                }
                                // Priority 2: Morph images
                                else if (morph?.cover_display_url) {
                                  imageUrl = buildAssetUrl(morph.cover_display_url);
                                  source = 'morph.cover_display_url';
                                } else if (morph?.cover_thumb_url) {
                                  imageUrl = buildAssetUrl(morph.cover_thumb_url);
                                  source = 'morph.cover_thumb_url';
                                } else if (morph?.image_url) {
                                  imageUrl = buildAssetUrl(morph.image_url);
                                  source = 'morph.image_url';
                                }
                                // Priority 3: Species images
                                else if (species?.cover_display_url) {
                                  imageUrl = buildAssetUrl(species.cover_display_url);
                                  source = 'species.cover_display_url';
                                } else if (species?.cover_thumb_url) {
                                  imageUrl = buildAssetUrl(species.cover_thumb_url);
                                  source = 'species.cover_thumb_url';
                                } else if (species?.image_url) {
                                  imageUrl = buildAssetUrl(species.image_url);
                                  source = 'species.image_url';
                                }
                                
                                // Fallback to placeholder
                                const finalUrl = imageUrl || NO_PHOTO_SRC;
                                
                                return (
                                  <img 
                                    src={finalUrl}
                                    alt={item.name} 
                                    className="me-2" 
                                    data-no-photo={imageUrl ? undefined : '1'}
                              onError={imageUrl ? retryThenTile : undefined}
                                    style={{ width: '32px', height: '32px', objectFit: imageUrl ? 'cover' : 'contain', borderRadius: '4px' }}
                                  />
                                );
                              })()}
                              <div>
                                <div className="fw-medium">{item.name || 'Product'}</div>
                                {/* I-02d — lot lines list their members;
                                    I-02d.2 pool bundles show Group of N
                                    (no members — pulled from stock). */}
                                {item.lot?.lot_type === 'pool_group' ? (
                                  <small className="text-muted d-block">
                                    Group of {item.lot.quantity} — pulls from available stock
                                  </small>
                                ) : item.lot?.members?.length > 0 && (
                                  <small className="text-muted d-block">
                                    {/* Cowork F1 — name or #SHORTID · ~age */}
                                    {item.lot.members.map((m) => {
                                      const glyph = m.gender === 0 ? '♂' : m.gender === 1 ? '♀' : '?';
                                      return `${glyph} ${memberDisplayLabel(m)}`;
                                    }).join(' · ')}
                                  </small>
                                )}
                                <small className="text-muted">
                                  {formatCurrency(item.unit_price)} × {item.quantity} = {formatCurrency(item.line_total)}
                                </small>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p className="text-muted">Order items loading...</p>
                )}
                
                <hr />
                
                {/* Display server-provided totals only */}
                {orderData.subtotal && orderData.shipping_total && orderData.order_total ? (
                  <>
                    <div className="d-flex justify-content-between">
                      <span>Subtotal:</span>
                      <span>{formatCurrency(orderData.subtotal)}</span>
                    </div>

                    {/* Phase C: Discount Display.
                        adv-review S1 (2026-08-10): total_discount is
                        ITEM-only, so a shipping-only promo left this
                        block hidden while the total already included the
                        discount — a money page whose lines didn't add up.
                        The shipping figure lives in the usage rows. */}
                    {((orderData.total_discount && parseFloat(orderData.total_discount) > 0)
                      || shippingDiscountOf(orderData) > 0) && (
                      <div className="discount-breakdown">
                        <div className="discount-breakdown__title">
                          Discounts Applied
                        </div>
                        {orderData.auto_discount && parseFloat(orderData.auto_discount) > 0 && (
                          <div className="discount-breakdown__row">
                            <span>Automatic discount:</span>
                            <span className="discount-breakdown__amount">-{formatCurrency(orderData.auto_discount)}</span>
                          </div>
                        )}
                        {orderData.promo_code_discount && parseFloat(orderData.promo_code_discount) > 0 && (
                          <div className="discount-breakdown__row">
                            <span>Promo code:</span>
                            <span className="discount-breakdown__amount">-{formatCurrency(orderData.promo_code_discount)}</span>
                          </div>
                        )}
                        <div className="discount-breakdown__row discount-breakdown__row--total">
                          <span>Total discount:</span>
                          <span className="discount-breakdown__amount">-{formatCurrency(orderData.total_discount)}</span>
                        </div>
                        {(() => {
                          // Punch #68 (Cowork 2026-08-10): checkout showed
                          // the promo AMOUNT but never the offer's NAME —
                          // this block did `.length` on what is a DICT for
                          // an unpaid order ({_pending, usages}), so it
                          // never rendered. Normalize both shapes: the
                          // pending usages (awaiting payment) and the
                          // final list snapshot (paid).
                          // Cowork run 6: shipping offers listed here sat
                          // under "Total discount", a heading that doesn't
                          // include their money. They render beside the
                          // Shipping discount line instead — namedPromotions
                          // filters them (and matches the paid envelope's
                          // bare type:'SHIPPING_DISCOUNT' rows the old
                          // inline filter missed, which double-listed the
                          // offer on a revisited PAID order).
                          const offers = namedPromotions(orderData.applied_promotions);
                          if (!offers.length) return null;
                          return (
                            <div className="discount-breakdown__promos">
                              {offers.map((promo, idx) => (
                                <div key={idx} className="discount-breakdown__promo-line">
                                  • {promo.name}
                                  {Number.isFinite(promo.amount) && (
                                    <span className="discount-breakdown__amount">
                                      {' '}−{formatCurrency(promo.amount)}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    <div className="d-flex justify-content-between">
                      <span>
                        Estimated Shipping ({quoteData?.service_name || orderData.shipping_method || 'FedEx Priority Overnight'}):
                      </span>
                      <span>{formatCurrency(orderData.shipping_total)}</span>
                    </div>
                    {shippingDiscountOf(orderData) > 0 && (
                      <div className="d-flex justify-content-between" style={{ fontSize: '0.875rem' }}>
                        <span>
                          Shipping discount{shippingOfferName(orderData) ? ` (${shippingOfferName(orderData)})` : ''}:
                        </span>
                        <span style={{ color: 'var(--ff-semantic-success)' }}>
                          -{formatCurrency(shippingDiscountOf(orderData))}
                        </span>
                      </div>
                    )}

                    <hr />

                    <div className="d-flex justify-content-between fw-bold">
                      <span>Estimated Total:</span>
                      <span>{formatCurrency(orderData.order_total)}</span>
                    </div>
                  </>
                ) : (
                  <div className="text-center text-muted">
                    <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                    Loading order totals...
                  </div>
                )}
              </Card.Body>
            </Card>

          </Col>
        </Row>

        {/* Payment Section */}
        <Row className="mt-4">
          <Col md={8}>
            {/* Stripe Configuration Error */}
            {stripeConfigError && (
              <Alert variant="warning" className="mb-4" dismissible onClose={() => setStripeConfigError(false)}>
                <Alert.Heading>⚙️ Stripe Configuration Missing</Alert.Heading>
                <p>
                  Please add <code>REACT_APP_STRIPE_PUBLISHABLE_KEY</code> to your <code>.env</code> file 
                  and restart the development server to enable payments.
                </p>
              </Alert>
            )}
            

            {/* Payment Error */}
            {paymentError && (
              <Alert variant="danger" className="mb-4">
                {paymentError}
                <div className="mt-2">
                  {/* a retry is not destructive — the ordinary (secondary)
                      action, never a red outline */}
                  <button
                    type="button"
                    className="ff-btn ff-btn--secondary ff-btn--sm"
                    onClick={() => initiatePaymentFlow(orderData.id)}
                    disabled={loading || isProcessingPayment}
                  >
                    {isProcessingPayment ? 'Processing...' : 'Try Again'}
                  </button>
                </div>
              </Alert>
            )}


            {/* Stripe Payment Form */}
            {clientSecret && paymentStep === 'payment' && stripePromise && (
              <Card className="mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5>💳 Payment Information</h5>
                </Card.Header>
                <Card.Body>
                  <Elements 
                    stripe={stripePromise} 
                    options={{ 
                      clientSecret,
                      appearance: {
                        theme: 'stripe'
                      }
                    }}
                  >
                    <PaymentForm
                      key={`payment-form-${orderData.id}-${clientSecret?.substring(0, 10)}-${addressData?.billing ? 'bd' : 'no-bd'}`}
                      orderTotal={orderData.order_total}
                      orderId={orderData.id}
                      quoteData={quoteData}
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                      clientSecret={clientSecret}
                      billingDetails={addressData?.billing ? {
                        name: `${addressData.billing.first_name || ''} ${addressData.billing.last_name || ''}`.trim() || undefined,
                        email: user?.email || undefined,
                        phone: addressData.billing.phone || orderData?.contact_phone || undefined,
                        address: {
                          line1: addressData.billing.address_line1 || undefined,
                          line2: addressData.billing.address_line2 || undefined,
                          city: addressData.billing.city || undefined,
                          state: addressData.billing.state || undefined,
                          postal_code: addressData.billing.zip || undefined,
                          country: 'US',
                        },
                      } : null}
                    />
                  </Elements>
                </Card.Body>
              </Card>
            )}

            {/* Processing Payment */}
            {paymentStep === 'polling' && (
              <Card className="mb-4 border-info">
                <Card.Body className="text-center">
                  <FroggySpinner size="md" label="Processing" />
                  <h5 className="text-info">Payment submitted successfully!</h5>
                  <p>Updating your order status...</p>
                  <small className="text-muted">This may take a few moments</small>
                </Card.Body>
              </Card>
            )}

            {/* Payment Success */}
            {paymentStep === 'success' && (
              <Card className="mb-4 border-success">
                <Card.Body className="text-center">
                  <div className="text-success mb-3">
                    <i className="bi bi-check-circle" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h4 className="text-success">Payment Received!</h4>
                  <p>Thank you for your order. We'll email you to schedule pickup.</p>
                  <p><small className="text-muted">Order ID: {orderData.id}</small></p>
                </Card.Body>
              </Card>
            )}

            {/* Fallback: Show button if no payment flow initiated */}
            {paymentStep === 'init' && !loading && orderData && orderData.status === 'AWAITING_PAYMENT' && orderData.payment_status !== 'PAID' && (
              /* Design System v1 §2.2 (Phase 3): the step's ONE primary,
                 and the way back to the address step as a ghost — the
                 same row the address step ends on. */
              <div className="checkout-submit-actions">
                <Link to="/checkout/address" className="ff-btn ff-btn--ghost checkout-back">
                  ← Back to address
                </Link>
                <button
                  type="button"
                  className="ff-btn ff-btn--primary checkout-pay-start"
                  onClick={() => initiatePaymentFlow(orderData.id)}
                  disabled={loading || isProcessingPayment}
                >
                  {isProcessingPayment ? 'Processing...' : 'Begin Payment Process'}
                </button>
              </div>
            )}

            {/* Already Paid Orders */}
            {orderData && orderData.payment_status === 'PAID' && (
              <Alert variant="success" className="text-center">
                <p>This order has already been paid. <strong>Order #{orderData.id}</strong></p>
                <button
                  type="button"
                  className="ff-btn ff-btn--secondary"
                  onClick={() => navigate(`/order-confirmation/${orderData.id}`)}
                >
                  View Order Confirmation
                </button>
              </Alert>
            )}
            
            {/* Order Status Not Ready for Payment */}
            {orderData && orderData.status !== 'AWAITING_PAYMENT' && orderData.payment_status !== 'PAID' && (
              <Alert variant="info" className="text-center">
                <p>Payment not available for this order status: <strong>{statusLabel(orderData.status)}</strong></p>
              </Alert>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CheckoutPaymentPage;